import { useCallback, useEffect, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/tables/DataTable';
import { ROUTES } from '../../constants/routes';
import { deleteSite, getSitesWithCounts } from '../../services/siteService';
import type { Site, SiteWithCounts } from '../../types/project';
import { sitesColumns } from './sitesColumns';
import { NewSiteDialog } from './NewSiteDialog';
import { EditSiteDialog } from './EditSiteDialog';
import { DeleteSiteDialog } from './DeleteSiteDialog';
import { usePermissions } from '../../hooks/usePermissions';

export const SitesPage = () => {
  const navigate = useNavigate();
  const { canManageEntities } = usePermissions();
  const [data, setData] = useState<SiteWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('SiteName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [editSite, setEditSite] = useState<Site | null>(null);
  const [deleteSiteState, setDeleteSiteState] = useState<SiteWithCounts | null>(null);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const sites = await getSitesWithCounts();
      setData(sites);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    const siteKey = row.SiteKey as string;
    navigate(ROUTES.SITE_DETAIL.replace(':siteKey', siteKey));
  };

  const handleEditClick = (row: Record<string, unknown>) => {
    const site = data.find((s) => s.SiteKey === row.SiteKey) ?? null;
    setEditSite(site);
  };

  const handleDeleteClick = (row: Record<string, unknown>) => {
    const site = data.find((s) => s.SiteKey === row.SiteKey) ?? null;
    setDeleteSiteState(site);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteSiteState) return;
    await deleteSite(deleteSiteState.SiteKey);
    setDeleteSiteState(null);
    fetchSites();
  };

  const handleCreated = () => {
    setNewOpen(false);
    fetchSites();
  };

  const handleUpdated = () => {
    setEditSite(null);
    fetchSites();
  };

  const filtered = data.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.SiteName.toLowerCase().includes(q)
      || (s.SiteCity || '').toLowerCase().includes(q)
      || (s.Province || '').toLowerCase().includes(q)
      || (s.Country || '').toLowerCase().includes(q);
  });

  const sortedData = [...filtered].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sortBy];
    const bVal = (b as unknown as Record<string, unknown>)[sortBy];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 800, color: 'text.primary', letterSpacing: '-0.025em',
          fontSize: '1.75rem', lineHeight: 1.2,
        }}>
          Sites
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search sites..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
          }}
          sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8125rem' } }}
        />
        {canManageEntities && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}
            size="small" sx={{
              boxShadow: '0 2px 8px rgba(74,103,65,0.2)', borderRadius: '10px',
              textTransform: 'none', fontWeight: 600, px: 2.5, py: 0.75,
              fontSize: '0.8125rem', letterSpacing: '-0.005em',
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: '0 4px 14px rgba(74,103,65,0.3)', transform: 'translateY(-1px)' },
              '&:active': { transform: 'translateY(0)' },
            }}>
            New Site
          </Button>
        )}
      </Box>

      <DataTable
        columns={sitesColumns}
        data={sortedData as unknown as Record<string, unknown>[]}
        loading={loading}
        onRowClick={handleRowClick}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      <NewSiteDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={handleCreated} />
      <EditSiteDialog open={!!editSite} site={editSite} onClose={() => setEditSite(null)} onUpdated={handleUpdated} />
      <DeleteSiteDialog
        open={!!deleteSiteState}
        site={deleteSiteState}
        onClose={() => setDeleteSiteState(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default SitesPage;
